import { css } from "@emotion/css";
import { Callout, CheckboxNew, ExternalLink } from "@octopusdeploy/design-system-components";
import { DebouncedTextWithLabel as DebounceText } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { ProcessTemplateParameterResource } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import ControlTypeSelector from "~/components/ControlType/ControlTypeSelector";
import MarkdownEditor from "~/components/form/MarkdownEditor/MarkdownEditor";
import { required } from "~/components/form/Validators";
import Note from "~/primitiveComponents/form/Note/Note";
const CONTROL_TYPE_KEY = "Octopus.ControlType";
const SELECT_OPTIONS_KEY = "Octopus.SelectOptions";
interface ParameterConfigurationViewProps {
    model: ProcessTemplateParameterResource;
    setModel: (updates: Partial<ProcessTemplateParameterResource>) => void;
    getFieldError: (fieldName: string) => string | undefined;
    isEditing: boolean;
    disableControlType?: boolean;
    processTemplateLink?: string;
    allParameters: ProcessTemplateParameterResource[];
    originalName: string;
}
export const ParameterConfigurationView: React.FC<ParameterConfigurationViewProps> = (props) => {
    const { model, setModel, getFieldError, isEditing, disableControlType, processTemplateLink, allParameters, originalName } = props;
    const showRenameWarning = originalName && originalName !== model.Name;
    const updateModel = (newValues: Partial<ProcessTemplateParameterResource>) => setModel(newValues);
    const updateDisplaySetting = (key: string, value: string | string[]) => updateModel({
        DisplaySettings: {
            ...model.DisplaySettings,
            [key]: value,
        },
    });
    return (<div className={styles.container}>
            {showRenameWarning && (<Callout type={"warning"} title={"Potential data loss"}>
                    <div>
                        <p>If you rename a parameter, you'll lose any data associated with it in existing steps. You should ensure all usages are updated to use the new name.</p>
                        {processTemplateLink && (<p>
                                We recommend renaming parameters via the <ExternalLink href={processTemplateLink}>template's source code</ExternalLink>.
                            </p>)}
                    </div>
                </Callout>)}

            {!isEditing && (<ControlTypeSelector disableSelect={disableControlType} excludedControlTypeOptions={[]} controlType={model.DisplaySettings[CONTROL_TYPE_KEY]} selectOptions={model.DisplaySettings[SELECT_OPTIONS_KEY]} onControlTypeChange={(controlType) => updateDisplaySetting(CONTROL_TYPE_KEY, controlType)} onSelectOptionsChange={(selectOptions) => updateDisplaySetting(SELECT_OPTIONS_KEY, selectOptions)} controlTypeError={getFieldError("ControlType")} selectOptionsError={getFieldError("SelectOptions")} hideDescription={true}/>)}

            <DebounceText value={model.Name} onChange={(Name) => updateModel({ Name })} label="Parameter name" error={getFieldError("Name")} validate={required("Please enter a parameter name")}/>
            <Note>Can contain letters, digits, dashes and periods.</Note>

            <DebounceText value={model.Label} onChange={(Label) => updateModel({ Label })} label="Label (Optional)"/>
            <Note>
                The parameter label shown in the project e.g. <code>Server name</code>.
            </Note>

            <MarkdownEditor value={model.HelpText} onChange={(HelpText) => updateModel({ HelpText })} label="Help text (Optional)"/>
            <Note style={{ marginBottom: "16px" }}>Additional instructions or context that would be helpful for this field.</Note>
            <CheckboxNew value={model.IsOptional ?? false} label={"Parameter is optional in the project"} description={"For projects where the value of this parameter is not necessary. The field will be marked as optional."} onChange={(IsOptional) => updateModel({ IsOptional })}/>
        </div>);
};
ParameterConfigurationView.displayName = "ParameterConfigurationView"
const styles = {
    // Horizontal padding to prevent content from being cut off on the sides
    container: css({
        padding: space[4],
    }),
};
